<template>
    <div class="content-widget">
        <div
            id="plot-grid"
            ref="plot-grid"
            class="plot-grid-widget"
            :class="{ 'fixed': $vuetify.breakpoint.md || $vuetify.breakpoint.sm }"
        >
            <Loader
                v-if="isLoadingCutaway"
                :messageType="'loading-cutaway-view'"
            />

            <template v-else>
                <CutawayHeader
                    v-if="!isAlternativeMode && data.floors"
                    :widget="'plot-grid'"
                    :isOpenedSidebar="isOpenedSidebar"
                    :plotCount="plotCount"
                />

                <!-- Desktop + tablet layout-->
                <v-sheet
                    v-if="!$vuetify.breakpoint.smAndDown || ($vuetify.breakpoint.sm && isLandscape)"
                    class="mx-8 mt-16"
                >
                    <PlotGridTable
                        v-if="data.lotHeader && unicPlotList.length > 0"
                        :currentPage="currentPage"
                        :areaUnit="data.areaUnit"
                        :isOpenedSidebar="isOpenedSidebar"
                        :isLandscape="isLandscape"
                        :headers="data.lotHeader"
                        :plotList="unicPlotList"
                        :plotPerPage="plotPerPage"
                        :programUuid="data.program.id"
                        @update-widget="goToPlot"
                    />
                </v-sheet>
                <!-- /Desktop + tablet layout-->

                <!-- Mobile layout -->
                <v-sheet
                    v-else
                    justify="center"
                    class="d-flex flex-column mt-8 mx-4"
                    no-gutters
                >
                    <div
                        v-for="(plot, index) in filteredPlot"
                        :key="`plot-${index}`"
                        class="plot-grid-card pb-12"
                        :style="{ 'min-width': $vuetify.breakpoint.xsOnly ? '300px' : '450px' }"
                    >
                        <PlotGridCard
                            :class="{'mt-16' : index === 0}"
                            :plot="plot"
                            :type="'plot-grid'"
                            @update-widget="goToPlot"
                        />
                    </div>
                </v-sheet>
            </template>

            <PlotGridPagination
                v-if="unicPlotList"
                :plotListLength="unicPlotList.length"
                :plotPerPage="plotPerPage"
                @update-page="updatePage"
            />
        </div>
    </div>
</template>

<script>
    import _ from 'lodash';
    import { EventBus } from '@/event-bus';
    import { mapActions, mapGetters } from 'vuex';
    import Http from '@/api/data';

    import Loader from '@/components/Loader';
    import PlotGridCard from '@/components/plotGrid/PlotGridCard';
    import PlotGridTable from '@/components/plotGrid/PlotGridTable';
    import PlotGridPagination from '@/components/plotGrid/PlotGridPagination';
    import CutawayHeader from "@/components/cutawayHeader/CutawayHeader";

    export default {
        name: 'PlotGrid',
        components: {
            Loader,
            PlotGridCard,
            PlotGridTable,
            PlotGridPagination,
            CutawayHeader
        },
        props: [
            'data',
            'standalone',
            'active',
            'standalone',
            'isOpenedSidebar'
        ],
        computed: {
            ...mapGetters([
                'language',
                'logos',
                'protocol',
                'isAlternativeMode',
                'isIframe',
                'isReviewUse',
                'isLocalstorageAvailable'
            ]),
            filteredPlot() {
                let firstPlot = (this.currentPage - 1) * this.plotPerPage;
                let lastPlot = this.currentPage === 1 ? this.plotPerPage : (firstPlot + this.plotPerPage);
                return this.unicPlotList.slice(firstPlot, lastPlot);
            },
            plotCount () {
                let hotspotListCount = 0;
                let activeFloors = this.$props.data.filters ? this.$props.data.filters.floors.filter(floor => floor.active) : [];
                if (activeFloors.length) {
                    activeFloors.forEach(activeFloor => {
                        let floor = this.$props.data.floors.filter(floor => floor.id === activeFloor.id);
                        if (floor.length)
                            hotspotListCount += floor[0].hotspots.length;
                    })
                } else
                    hotspotListCount = this.$props.data.floors ? this.getHotspotList().filter(el => el.visible).length : 0;

                return hotspotListCount;
            },
            unicPlotList() {
                let plotList = [];
                let nameList = [];
                this.plotList.forEach(plot => {
                    if (!nameList.includes(plot.name)) {
                        plotList.push(plot);
                        nameList.push(plot.name);
                    }
                });
                return plotList;
            },
            plotPerPage() {
                // if (this.$vuetify.breakpoint.mdAndUp)
                //     return 10;
                // else
                //     return 5;
                // TODO: adjust row count by screen size
                return 5;
            },
            hasSharedFavlist() {
                return this.$route.query.favlist ? true : false;
            },
            favoriteKey() {
                return `favorite-${this.$route.query.id}`;
            },
            apiUrl() {
                return 'https://' + (this.isReviewUse ? process.env.API_LIST.REVIEW : process.env.API_LIST.FO);
            }
        },
        data() {
            return {
                plotGridOptions: [
                    'pricegrid',
                    'both'
                ],
                urlLink: '',
                availabilityTypeSortOrder : [
                    1,
                    3,
                    4,
                    5,
                    2,
                    100,
                    101,
                    102,
                    103,
                    104,
                    105,
                    106,
                    107,
                    108
                ],
                widgetId: null,
                currentPage: 1,
                plotList: [],
                isLandscape: false,
                squareMeterValue: .093,
                isLoadingCutaway: false
            };
        },
        mounted() {
            if (this.hasSharedFavlist && this.isLocalstorageAvailable) {
                localStorage.setItem(this.favoriteKey, JSON.stringify(decodeURI(this.$route.query.favlist).split(',')));
                if (this.isAlternativeMode && this.$props.data.type === 'plot-grid')
                    this.filterHotspot(this.$props.data.filters);
            }

            this.render();

            window.addEventListener('resize', () => {
                if (!this.isIframe)
                    this.isLandscape = window.matchMedia('(orientation: landscape)').matches;
            });

            EventBus.$on('plotgrid_FilterHotspot', (filters) => this.filterHotspot(filters));

            EventBus.$on('filterPlotByPrice', () => {
                let hotspotList = this.getHotspotList();
                this.plotList = hotspotList;
                if (this.$props.data.filters.userPriceInterval) {
                    this.plotList = hotspotList.filter((plot) => {
                        let currentPrice = this.getKeyValue('price', plot) ? parseFloat(this.getKeyValue('price', plot).match(/\d+/g).join('')) : null;
                        return currentPrice >= this.$props.data.filters.userPriceInterval.min && currentPrice <= this.$props.data.filters.userPriceInterval.max || currentPrice === null;
                    });
                }
                this.plotList = this.plotList.filter(plot => plot.visible);
            });
        },
        methods: {
            ...mapActions([
                'setIsLoading',
                'setLogo'
            ]),
            ...mapActions('widget', [
                'setPlotCount'
            ]),
            render() {
                // Start loading animation for cutaway-view-widget
                this.isLoadingCutaway = true;

                if (_.isEmpty(this.$props.data) || !this.$props.data.floors) {
                    this.urlLink = this.$props.link ? this.$props.link : this.apiUrl + '/request-data/cutaway-view' + window.location.search;
                    if (this.language && !this.urlLink.includes('&language'))
                        this.urlLink += '&language=' + this.language;
                    if (this.isOfflineMode && !this.urlLink.includes('&offline'))
                        this.urlLink += '&offline';

                    this.widgetId = new URL(this.urlLink).searchParams.get('id');
                    let currentHotspotList;
                    let hotspotNameList = [];
                    Http.getData(this.urlLink).then(payload => {
                        if (payload) {
                            // Rename key 'levels' to 'floors'
                            delete Object.assign(payload, {['floors']: payload['levels'] })['levels'];
                            payload.id = this.widgetId;
                            payload.type = 'plot-grid';
                            payload.gui.type = 'cutaway';
                            payload = Object.assign(payload, { widgetTitle: payload.widgetTitle });

                            if (this.$props.data.language)
                                this.$i18n.locale = payload.language;

                            if (payload.options && 'showResults' in payload.options)
                                this.setPlotCount(payload.options.showResults);

                            let plotList = [];
                            let availableFloors = [];
                            payload.floors.forEach(floor => {
                                if (floor.hotspots)
                                    if (plotList.length >= 1)
                                        plotList = plotList.concat(floor.hotspots);
                                    else
                                        plotList = floor.hotspots;

                                // Get unique plot counter by floor
                                currentHotspotList = [];
                                floor.hotspots.forEach(hotspot => {
                                    if (!hotspotNameList.includes(hotspot.name)) {
                                        currentHotspotList.push(hotspot);
                                        hotspotNameList.push(hotspot.name);
                                    }
                                });

                                // Get each floor
                                availableFloors.push({
                                    'id': floor.id,
                                    'text': floor.text,
                                    'hotspots': currentHotspotList,
                                    'active': false
                                });
                            });

                            //Sort by availability type
                            this.plotList = this.sortByAvailability(plotList);

                            let areaFilter = {};
                            payload.areaUnit = this.getAreaUnit(plotList);
                            areaFilter[payload.areaUnit] = this.getInterval(plotList, 'surface');

                            if (!this.$props.data.filters)
                                payload.filters = {
                                    'floors': availableFloors,
                                    'priceInterval': this.getInterval(plotList, 'price'),
                                    'availabilities': this.getPlotAvailabilityList(plotList),
                                    'tags': this.getTagList(plotList),
                                    'typologies': this.getTypologyList(plotList),
                                    'searchValue': null,
                                    'areaInterval': this.language !== 'en' ? areaFilter : this.convertByUnit(areaFilter[payload.areaUnit])
                                };

                            if (this.plotGridOptions.includes(payload.lotView)) {
                                this.plotList = this.plotList.filter(plot => plot.visible);
                                if (this.standalone) {
                                    EventBus.$emit('setSidebarData', payload);
                                    EventBus.$emit('updateLoadedWidgets', {
                                        id: payload.type,
                                        link: this.urlLink,
                                        active: true,
                                        widgetType: payload.type
                                    });
                                    EventBus.$emit('savePayload', payload);
                                } else
                                    EventBus.$emit('updateSidebar', payload, false);
                            } else {
                                payload.type = 'plot-grid';
                                // First floor
                                payload.frames = payload.floors[0].frames;
                                payload.hotspots = payload.floors[0].hotspots;
                                payload.images = payload.floors[0].images;
                                EventBus.$emit('setSidebarData', payload);
                                EventBus.$emit('updateLoadedWidgets', {
                                    id: payload.type,
                                    link: this.urlLink,
                                    active: true,
                                    widgetType: payload.type
                                });
                            }

                            if (!this.logos || (payload.program.logo && (this.logos && !('program' in this.logos))))
                                this.setLogo({
                                    'type': 'program',
                                    'src': payload.program.logo
                                });

                            if (!this.logos  || (payload.promoter.logo && (this.logos && !('promoter' in this.logos))))
                                this.setLogo({
                                    'type': 'promoter',
                                    'src': payload.promoter.logo
                                });
                            this.isLoadingCutaway = false;
                        }
                    }).catch(error => {
                        EventBus.$emit('setErrorState', true, error);
                    });
                } else {
                    this.plotList = this.getHotspotList();
                    this.plotList = this.plotList.filter(plot => plot.visible);
                    let data = Object.assign(this.$props.data, {'type': 'plot-grid'});
                    EventBus.$emit('updateSidebar', data, false);
                    this.isLoadingCutaway = false;
                }
                this.setIsLoading(false);
            },
            getHotspotList() {
                let plotList = [];
                let unicPlotNameList = [];
                this.$props.data.floors.forEach(floor => {
                    floor.hotspots.forEach(hotspot => {
                        if (!unicPlotNameList.includes(hotspot.name)) {
                            plotList.push(hotspot);
                            unicPlotNameList.push(hotspot.name);
                        }
                    });
                });
                if (plotList.length)
                    plotList = this.sortByAvailability(plotList);
                return plotList;
            },
            sortByAvailability(hotspotList) {
                let result = [];
               
               // Sort by plot name
                hotspotList.sort((a, b) => {
                    var aA = a.name.replace(/[^a-zA-Z]/g, '');
                    var bA = b.name.replace(/[^a-zA-Z]/g, '');
                    if (aA === bA) {
                        var aN = parseInt(a.name.replace(/[^0-9]/g, ''), 10);
                        var bN = parseInt(b.name.replace(/[^0-9]/g, ''), 10);
                        return aN === bN ? 0 : aN > bN ? 1 : -1;
                    } else {
                        return aA > bA ? 1 : -1;
                    }
                });

                if (hotspotList) {
                    let tmpHotspotList = [];
                    let currentAvailability = null;
                    let currentAvailabilityType = null;
                    this.availabilityTypeSortOrder.forEach(key => {
                        hotspotList.forEach(hotspot => {
                            if (hotspot.name !== null) {
                                currentAvailability = this.getKeyValue('availability', hotspot);
                                currentAvailabilityType = this.getKeyType('availability', hotspot);
                                if (currentAvailability !== null && currentAvailabilityType === key)
                                    tmpHotspotList.push(hotspot);
                            }
                        });
                    });


                    let unsortedHotspotList = hotspotList.filter(hotspot => !this.availabilityTypeSortOrder.includes(this.getKeyType('availability', hotspot)));
                    result = tmpHotspotList.concat(unsortedHotspotList);
                }
                return result;
            },
            goToPlot(plot) {
                EventBus.$emit('saveSidebar');
                EventBus.$emit('updateLoadedWidgets', {
                    id: 'plot',
                    link: plot.link,
                    active: true
                });

                if (this.isAlternativeMode)
                    EventBus.$emit('closeSidebar');
            },
            getKeyValue(key, plot) {
                var result = plot.details.find(detail => detail.id === key);
                return result ? result['value'] : null;
            },
            getKeyType(key, plot) {
                var result = plot.details.find(detail => detail.id === key);
                return result ? result['type'] : null;
            },
            getAreaUnit(hotspotList) {
                let unit = null;
                if (hotspotList) {
                    hotspotList.forEach(hotspot => {
                        if (unit !== null)
                            return;

                        let area = hotspot.details.filter(detail => detail.id == 'surface');
                        if (area.length > 0) {
                            unit = area[0].unit;
                        }
                    });
                }
                return unit;
            },
            getInterval(hotspotList, type) {
                let intervals = {'min': 0, 'max': 0};
                if (hotspotList)
                    hotspotList.forEach((hotspot) => {
                        if (hotspot.visible === true && hotspot.name) {
                            let tmpValue =  this.getKeyValue(type, hotspot);
                            let currentValue = this.getFormatedValue(tmpValue, type);
                            intervals['max'] = intervals['max'] < currentValue ? currentValue + 1 : intervals['max'];
                        }
                    });
                return intervals;
            },
            convertByUnit(areaInterval) {
                return {
                    'sq ft': areaInterval,
                    'sq m': {
                        'min': 0,
                        'max': Math.round(((areaInterval['max'] * this.squareMeterValue) + Number.EPSILON) * 100) / 100
                    }
                };
            },
            getFormatedValue(value, type) {
                if (!value)
                    return 0;

                switch (type) {
                    case 'price':
                        return value ? parseFloat(value.match(/\d+/g).join('')) : 0;
                    case 'surface':
                        value = value.replace(/\s/g, '');
                        if (this.language === 'en')
                            value = value.replace(',', '');
                        else
                            value = value.replace('.', '');
                        return value ? parseFloat(value.match(/[0-9]+/g)) : 0;
                }
            },
            getPlotAvailabilityList(hotspotList) {
                if (hotspotList.length > 0) {
                    let availabilityList = {};
                    let currentAvailability = null;
                    let currentAvailabilityType = null;
                    let tmpAvailabilityList = {};
                    hotspotList.forEach((hotspot) => {
                        if (hotspot.name !== null) {
                            currentAvailability = this.getKeyValue('availability', hotspot);
                            currentAvailabilityType = this.getKeyType('availability', hotspot);
                            if (currentAvailability !== null) {
                                if (tmpAvailabilityList[currentAvailability]) {
                                    if (!tmpAvailabilityList[currentAvailability].hotspots.includes(hotspot.name))
                                        tmpAvailabilityList[currentAvailability].hotspots.push(hotspot.name);
                                } else
                                    tmpAvailabilityList[currentAvailability] = {
                                        color: hotspot.color,
                                        active: false,
                                        type: currentAvailabilityType,
                                        hotspots: [hotspot.name]
                                    };
                            }
                        }
                    });

                    // //Sort by availability type
                    this.availabilityTypeSortOrder.forEach((key) => {
                        var found = false;
                        Object.keys(tmpAvailabilityList).filter(availability => {
                            if (!found && tmpAvailabilityList[availability].type == key) {
                                availabilityList[availability] = tmpAvailabilityList[availability];
                                found = true;
                                return false;
                            } else
                                return true;
                        })
                    });

                    return availabilityList;
                } else
                    return {};
            },
            filterHotspot(filters) {
                let selectedLevels = [];

                filters.disabledTypologies = [];
                if (this.$props.data.search)
                    this.$props.data.search[0].items.forEach(typology => {
                        if (!typology.active)
                            filters.disabledTypologies.push(typology.text);
                    });

                filters.floors.forEach(floor => {
                    if (floor.active)
                        selectedLevels.push(floor.id)
                });
                let vm = this;

                EventBus.$emit('filterHotspots', filters, filteredFloors => {
                    var hotspotList = [];
                    filteredFloors.forEach(floor => {
                        if (selectedLevels.includes(floor.id) || selectedLevels.length === 0) {
                            if (hotspotList.length >= 1)
                                hotspotList = hotspotList.concat(floor.hotspots);
                            else
                                hotspotList = floor.hotspots;
                        }
                    });
                    vm.plotList = this.sortByAvailability(hotspotList.filter(plot => plot.visible));;
                });
            },
            getTypologyList(hotspotList) {
                if (hotspotList.length > 0) {
                    let typologyList = {};
                    let currentTypology = null;
                    let tmpTypologyList = {};
                    hotspotList.forEach(hotspot => {
                        if (hotspot.name !== null) {
                            currentTypology = this.getKeyValue('category', hotspot);
                            if (currentTypology !== null) {
                                if (tmpTypologyList[currentTypology]) {
                                    if (!tmpTypologyList[currentTypology].hotspots.includes(hotspot.name))
                                        tmpTypologyList[currentTypology].hotspots.push(hotspot.name);
                                } else {
                                    tmpTypologyList[currentTypology] = {
                                        active: false,
                                        hotspots: [hotspot.name]
                                    };
                                }
                            }
                        }
                    });

                    //Sort by typology text
                    Object.keys(tmpTypologyList).sort().forEach((key) => {
                        typologyList[key] = tmpTypologyList[key];
                    });

                    return typologyList;
                } else
                    return {};
            },
            getTagList(hotspotList) {
                let tagList = [], addedTagList = [];
                hotspotList.forEach(hotspot => {
                    let tags = hotspot.details && hotspot.details.filter(el => el.id === "tags" && el.tags !== null);
                    if (tags[0]) {
                        Object.entries(tags[0].tags).forEach(tag => {
                            if (!addedTagList.includes(tag[0])) {
                                tagList.push({ id: tag[0], color: tag[1].color, type: tag[1].type, active: false })
                                addedTagList.push(tag[0]);
                            }
                        });
                    }
                });

                if (tagList.length > 0)
                    tagList.sort((a, b) => (a.type > b.type) ? 1 : (a.type === b.type) ? ((a.id > b.id) ? 1 : -1) : -1 );

                return tagList;
            },
            updatePage(page) {
                this.currentPage = page;
            }
        },
        watch: {
            active() {
                if (this.$props.active) {
                    if (_.isEmpty(this.$props.data) || !this.$props.data.floors) {
                        let plotGridData = null;
                        EventBus.$emit('getLoadedWidget', ['plot-grid'], this.widgetId, result => {
                            plotGridData = result;
                        });
                        EventBus.$emit('updateSidebar', plotGridData, false);
                    }
                    else
                        this.render();
                }
            }
        }
    }
</script>

<style lang="scss">
    @import "@/assets/scss/components/_plot-grid.scss";
</style>



// WEBPACK FOOTER //
// src/views/PlotGrid.vue